import * as React from 'react'
import { connect } from 'react-redux'
import { updateSettings } from '../../store/authentication/actions'
import { AnyAction, Dispatch } from 'redux';
import { AppState } from '../../store'
import Button from '../../components/Button/Button'
import Panel from '../../components/Panel/Panel'
import ScrollToTopOnMount from '../../components/Effects/ScrollToTopOnMount';
import ListLoader from '../../components/Loaders/ListLoader';
import { Helmet } from 'react-helmet';
import { withTranslation, WithTranslation } from 'react-i18next';
import { ProjectTag, CurrentUser, DisplayableError, Settings } from '../../types';
import ResourceModalTable from '../../components/Resource/Modal/ResourceModalTable';
import ResourceModalTableRow from '../../components/Resource/Modal/ResourceModalTableRow';
import ResourceModalTableRowHeader from '../../components/Resource/Modal/ResourceModalTableRowHeader';
import ResourceModalTableRowData from '../../components/Resource/Modal/ResourceModalTableRowData';
import ResourceModalTableRowActions from '../../components/Resource/Modal/ResourceModalTableRowActions';
import { showConfirmModal, showProjectTagModal } from '../../store/modals/actions';
import Title from '../../components/Settings/Title';
import { ProjectTagsController } from '../../controllers';

interface IStateToProps {
  currentUser: CurrentUser
}

interface IDispatchToProps {
  updateSettings: typeof updateSettings
  showProjectTagModal: typeof showProjectTagModal
  showConfirmModal: typeof showConfirmModal
}

type IProps = IStateToProps & IDispatchToProps & WithTranslation
interface IState {
  projectTags: ProjectTag[]
  settings: Settings
  errors: DisplayableError[]
}

class Contacts extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props)

    const { currentUser: { workspace: { setting } } } = props

    this.state = {
      settings: setting,
      projectTags: [],
      errors: [],
    }

    this.onAddProjectTagClick = this.onAddProjectTagClick.bind(this)
    this.onEditProjectTagClick = this.onEditProjectTagClick.bind(this)
    this.onDeleteProjectTagClick = this.onDeleteProjectTagClick.bind(this)
  }

  componentWillMount() {
    this.fetchProjectTags().catch(console.error)
  }

  async fetchProjectTags() {
    try {
      const { project_tags } = await ProjectTagsController.getProjectTags({})

      this.setState({ projectTags: project_tags })
    } catch (ex) {
      console.error(ex)
    }
  }

  onAddProjectTagClick() {
    this.props.showProjectTagModal({
      projectTag: {},
      onSubmit: (projectTag) => {
        const projectTags = [...this.state.projectTags]
        this.setState({
          projectTags: [
            projectTag,
            ...projectTags
          ]
        })
      }
    })
  }

  onEditProjectTagClick(projectTag: ProjectTag) {
    this.props.showProjectTagModal({
      projectTag: { id: projectTag.id },
      onSubmit: (projectTag) => {
        const projectTags = [...this.state.projectTags]
        const projectTagIndex = projectTags.findIndex(t => t.id === projectTag.id);

        if (projectTagIndex !== -1) {
          projectTags[projectTagIndex] = projectTag

          this.setState({
            projectTags: [...projectTags]
          })
        }
      }
    })
  }

  onDeleteProjectTagClick(projectTag: ProjectTag) {
    const { t } = this.props

    this.props.showConfirmModal({
      title: t('ProjectTagsModal::Delete project tag'),
      description: t('ProjectTagsModal::You are about to delete this project tag. Are you sure?'),
      action: { label: t('ProjectTagsModal::Delete'), isDestructive: true },
      onConfirm: async () => {
        try {
          await ProjectTagsController.delete(projectTag.id)

          const projectTags = [...this.state.projectTags];
          const projectTagIndex = projectTags.findIndex(t => t.id === projectTag.id);

          projectTags.splice(projectTagIndex, 1);

          this.setState({ projectTags: [...projectTags] })
        } catch (ex) {
          console.error(ex)
        }
      }
    })

  }

  render() {
    const { t } = this.props
    const { projectTags, errors } = this.state

    if (projectTags) {
      return (
        <>
          <Helmet>
            <title>{t('SettingContacts::{{__appName}} | Projects')}</title>
          </Helmet>
          <ScrollToTopOnMount />

          <Title>{t('SettingContacts::Projects')}</Title>

          <Panel>
            <div className='grid-cell with-12col'>
              <div className='form-item'>
                <label>{t('SettingContacts::Tags')}</label>
                {projectTags.length > 0 && <ResourceModalTable>
                  <ResourceModalTableRow header={true}>
                    <ResourceModalTableRowHeader stickyTop='0px' sticky>
                      {t('Contacts::Name')}
                    </ResourceModalTableRowHeader>
                    <ResourceModalTableRowHeader stickyTop='0px' sticky>
                      {t('Contacts::Project count')}
                    </ResourceModalTableRowHeader>
                    <ResourceModalTableRowHeader style={{ width: 40 }} stickyTop='0px' sticky />
                  </ResourceModalTableRow>

                  {projectTags.map((projectTag, index) => (
                    <ResourceModalTableRow key={index}>
                      <ResourceModalTableRowData>
                        {projectTag.name}
                      </ResourceModalTableRowData>
                      <ResourceModalTableRowData>
                        {projectTag.project_count}
                      </ResourceModalTableRowData>
                      {projectTag.id && <ResourceModalTableRowActions
                        actions={[
                          { key: 'edit', icon: 'edit-solid', content: t('Contacts::Edit'), onClick: () => this.onEditProjectTagClick(projectTag) },
                          { key: 'delete', icon: 'trash-alt-solid', content: t('Contacts::Delete'), destructive: true, onClick: () => this.onDeleteProjectTagClick(projectTag) },
                        ]}
                      />}
                    </ResourceModalTableRow>
                  ))}
                </ResourceModalTable>}

                <div className='form-table-footer' style={{ border: 'none' }}>
                  <Button type='default' icon='plus' text={t('Contacts::Add tag')} onClick={this.onAddProjectTagClick} />
                </div>
              </div>
            </div>
          </Panel>
        </>
      )
    }
    else {
      return (
        <Panel>
          <ListLoader />
        </Panel>
      )
    }
  }
}

const mapStateToProps = (state: AppState): IStateToProps => {
  const {
    authentication: {
      currentUser,
    }
  } = state

  return {
    currentUser: currentUser,
  }
}


const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IDispatchToProps => {
  return {
    updateSettings: (settings: Settings) => dispatch(updateSettings(settings)),
    showProjectTagModal: (options) => dispatch(showProjectTagModal(options)),
    showConfirmModal: (options) => dispatch(showConfirmModal(options))
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(Contacts))